import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";

const ImageLibraryModal = ({ isOpen, onClose, onSelectImage }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImageId, setSelectedImageId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadImages();
    }
  }, [isOpen]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getModelContentImages();
      if (response.success) {
        // Only show completed images
        const completedImages = (response.data.images || []).filter(
          img => img.processing_status === 'completed'
        );
        setImages(completedImages);
      }
    } catch (err) {
      console.error("Failed to load images:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = images.filter(img =>
    img.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInsert = () => {
    if (selectedImageId) {
      const imageUrl = apiClient.getModelContentImageUrl(selectedImageId);
      const selectedImage = images.find(img => img.document_id === selectedImageId);
      onSelectImage(imageUrl, selectedImage?.document_name || 'Image');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-slate-900">
            Select Image from Library
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <FontAwesomeIcon
              icon="search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading images..." />
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon="image" className="text-4xl text-slate-300 mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm ? "No images found" : "No images available"}
              </h4>
              <p className="text-slate-600">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Upload images in the Admin Panel > Model Content tab first"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image) => (
                <div
                  key={image.document_id}
                  onClick={() => setSelectedImageId(image.document_id)}
                  className={`relative cursor-pointer rounded-lg border-2 transition-all hover:shadow-lg ${
                    selectedImageId === image.document_id
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
                    <img
                      src={apiClient.getModelContentImageUrl(image.document_id)}
                      alt={image.document_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                        e.target.classList.add('opacity-50');
                      }}
                    />
                  </div>
                  <div className="p-3 bg-white rounded-b-lg">
                    <p className="text-sm font-medium text-slate-900 truncate" title={image.document_name}>
                      {image.document_name}
                    </p>
                    {image.description && (
                      <p className="text-xs text-slate-500 truncate mt-1" title={image.description}>
                        {image.description}
                      </p>
                    )}
                  </div>
                  {selectedImageId === image.document_id && (
                    <div className="absolute top-2 right-2 bg-primary-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                      <FontAwesomeIcon icon="check" className="text-sm" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-slate-600">
            {selectedImageId ? (
              <span className="font-medium text-slate-900">1 image selected</span>
            ) : (
              <span>Select an image to insert</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={!selectedImageId}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              <FontAwesomeIcon icon="plus" className="mr-2" />
              Insert Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLibraryModal;
